import { apply, burst, celebrate, toast } from '../core/effects';
import { typhoonDay } from './weather';
import { bus } from '../core/bus';
import { hasGame, S } from '../core/game';
import { dayId, type GameState } from '../core/state';
import { m } from '../i18n';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { dialogOpen, type Choice } from '../ui/dialog';
import { layers } from '../ui/layers';
import { openWorksheet } from '../ui/worksheet';
import { px } from '../world/mapdef';
import { ringNow } from './calls';
import { logJournal, practice } from './journal';
import { dayNumber, logSpell, progress } from './passport';
import { registerTalk } from './registry';
import { EVENTS, contestWins, misspellings, pickEvent, type EventContext, type EventId } from './clsPlan';
import { line, note, phrase, shuffled } from './clsTalk';

/**
 * School events: real things that happen in a kid's week — forgotten homework,
 * lost things, misunderstandings, bullying, contests, quizzes and the parents'
 * meeting. A weighted scheduler gives each day at most one, based on conditions.
 */

const H = (h: number, mm = 0) => h * 60 + mm;

/** Bookkeeping lives in `npcTalk` under `cls.*` (numbers that persist across days). */
function context(s: GameState): EventContext {
  const lastSeen: EventContext['lastSeen'] = {};
  EVENTS.forEach((id) => {
    const v = s.npcTalk[`cls.seen.${id}`];
    if (v !== undefined) lastSeen[id] = v;
  });
  return { day: dayId(s), week: s.week, dayNum: dayNumber(), homeworkDay: s.npcTalk['cls.hwDay'] ?? -1, lastSeen };
}

/** Today's event, picked once per day (lazily, so loading a save mid-day keeps it). */
export function todayEvent(): EventId | null {
  if (!hasGame()) return null;
  const s = S();
  if (typhoonDay()) return null;
  const d = dayNumber();
  if (s.npcTalk['cls.evDay'] !== d) {
    const ev = pickEvent(context(s), Math.random());
    s.npcTalk['cls.evDay'] = d;
    s.npcTalk['cls.ev'] = ev ? EVENTS.indexOf(ev) + 1 : 0;
    if (ev) s.npcTalk[`cls.seen.${ev}`] = d;
  }
  const i = s.npcTalk['cls.ev'] ?? 0;
  return i ? EVENTS[i - 1] : null;
}

const done = () => !!S().daily['cls.ev.done'];
const markDone = () => (S().daily['cls.ev.done'] = true);

function inClassroom(s: GameState): boolean {
  const p = s.player;
  return p.map === 'school' && p.x >= px(14) && p.x <= px(29) && p.y < px(12) + 6;
}

/** What the notice board says about today's event. */
export function eventNotice(): string | null {
  const ev = todayEvent();
  const t = m().classroom.events;
  if (ev === 'contest') return t.contest.notice;
  if (ev === 'parentsMeeting') return t.parentsMeeting.notice;
  if (ev === 'lostPencil' && !S().daily['cls.pencilFound']) return t.lostPencil.start;
  return null;
}

// ---------------------------------------------------------------------------
// Forgotten homework: stay honest.
// ---------------------------------------------------------------------------
function forgotHomework(): void {
  const t = m().classroom.events.forgotHomework;
  const P = m().classroom.phrases;
  const fix = (text: string) =>
    line('teacher', text, [
      {
        text: t.makeUp,
        run: () =>
          openWorksheet('homework', () => {
            celebrate();
            apply({ stars: 4, study: 6 });
            logJournal('📝', 'homeworkMadeUp');
            line('teacher', t.doneReply);
          }),
      },
      { text: t.later },
    ]);
  line('teacher', t.ask, [
    {
      text: t.honest,
      run: () => {
        note('teacher', 'honest');
        logJournal('📒', 'forgotHonest');
        apply({ stars: 3 }, { float: false });
        phrase('me', '', P.forgotHomework, () => fix(t.honestReply));
      },
    },
    {
      text: t.excuse,
      run: () => {
        logJournal('🐶', 'forgotExcuse');
        line('teacher', t.excuseReply, [{ text: t.admit, run: () => fix(t.admitReply) }]);
      },
    },
  ], '📒');
}

// ---------------------------------------------------------------------------
// Lost pencil case: found in the lost & found box.
// ---------------------------------------------------------------------------
export function lostPencilFound(): void {
  const t = m().classroom.events.lostPencil;
  S().daily['cls.pencilFound'] = true;
  markDone();
  sfx('pickup');
  celebrate();
  apply({ stars: 2, mood: 6 });
  logJournal('✏️', 'lostFound');
  line('narrator', t.found, undefined, '✏️', m().classroom.objects.cls_lostBox);
}

// ---------------------------------------------------------------------------
// Teacher–student misunderstanding: stay calm and explain.
// ---------------------------------------------------------------------------
function misunderstanding(): void {
  const t = m().classroom.events.misunderstanding;
  const P = m().classroom.phrases;
  let shouted = false;
  const calm = () =>
    phrase('me', '', P.misunderstanding, () =>
      line('me', t.explain, [
        {
          text: m().common.ok,
          run: () => {
            if (!shouted) note('teacher', 'calm');
            logJournal(shouted ? '😤' : '🕊️', shouted ? 'shouted' : 'calmExplained');
            celebrate();
            apply({ stars: shouted ? 3 : 6, mood: 4, minutes: 10 });
            line('teacher', t.calmReply);
          },
        },
      ], '🙂'),
    '🙂');
  const retry = (text: string) => line('teacher', text, [{ text: t.tryCalm, run: calm }]);
  line('teacher', t.ask, [
    { text: t.calm, run: calm },
    { text: t.shout, run: () => ((shouted = true), sfx('bad'), apply({ mood: -4 }), retry(t.shoutReply)) },
    { text: t.silent, run: () => retry(t.silentReply) },
  ], '😕');
}

// ---------------------------------------------------------------------------
// Bullying: Bo is teased in the park. Tell an adult or stand with him — never fight.
// ---------------------------------------------------------------------------
function bullyLive(): boolean {
  const s = S();
  return todayEvent() === 'bullying' && !done() && s.time >= H(9) && s.time < H(18);
}

function bullying(): void {
  const t = m().classroom.events.bullying;
  const P = m().classroom.phrases;
  const good = (flag: 'stood' | 'told', text: string) => {
    markDone();
    note('bo', flag);
    logJournal(flag === 'stood' ? '🛡️' : '🧑‍🤝‍🧑', flag === 'stood' ? 'bullyStood' : 'bullyTold');
    celebrate(true);
    burst('heart', 8);
    apply({ stars: 10, mood: 6, minutes: 15 });
    line('narrator', text, [{ text: m().common.ok, run: () => line('bo', t.boThanks, undefined, '🥹') }], '🌤️');
  };
  const choose = () =>
    line('narrator', t.open, [
      { text: t.stand, run: () => phrase('me', '', P.bullying, () => good('stood', t.standReply), '🛡️') },
      { text: t.tell, run: () => good('told', t.tellReply) },
      { text: t.fight, run: () => line('narrator', t.fightReply, [{ text: m().classroom.ui.tryAgain, run: choose }], '✋') },
      {
        text: t.ignore,
        run: () => {
          markDone();
          note('bo', 'ignored');
          logJournal('😔', 'bullyIgnored');
          sfx('bad');
          apply({ mood: -8 });
          line('narrator', t.ignoreReply, undefined, '😔');
        },
      },
    ], '😢');
  choose();
}

// ---------------------------------------------------------------------------
// Spelling bee: your answers + passport XP; a win or a setback with a growth-mindset reply.
// ---------------------------------------------------------------------------
function contestOpen(): boolean {
  const s = S();
  return todayEvent() === 'contest' && !done() && s.time >= H(14) && s.time < H(16, 30);
}

export function contestOffer(): Choice | null {
  if (todayEvent() !== 'contest') return null;
  const t = m().classroom.events.contest;
  if (done()) return { text: t.over, disabled: true };
  if (S().time < H(14)) return { text: t.notYet, disabled: true };
  return contestOpen() ? { text: t.join, run: spellingBee } : null;
}

function spellingBee(): void {
  const t = m().classroom.events.contest;
  markDone();
  const words = shuffled(t.words).slice(0, 3);
  let correct = 0;
  const round = (i: number) => {
    if (i >= words.length) return result();
    const w = words[i];
    const [a, b] = misspellings(w);
    const opts = shuffled([w, a, b]);
    const ask = () => {
      line('teacher', t.word(i + 1), [
        ...opts.map((o) => ({
          text: o,
          run: () => {
            if (o === w) {
              correct++;
              sfx('ok');
              logSpell(w.toLowerCase());
              toast(t.right);
            } else {
              sfx('bad');
              toast(t.wrong(w));
            }
            round(i + 1);
          },
        })),
        { text: t.hear, keepOpen: true, run: () => sayEnglish(w, 0.75) },
      ], '🐝');
      sayEnglish(w, 0.75);
    };
    ask();
  };
  const result = () => {
    const win = contestWins(progress().xp, correct, Math.random());
    practice('english');
    if (win) {
      celebrate(true);
      burst('sparkle', 20);
      apply({ stars: 12, mood: 10, study: 4, minutes: 30 });
      logJournal('🏅', 'contestWin');
      note('teacher', 'contestWin');
      line('narrator', t.win, [{ text: m().common.ok, run: () => line('teacher', t.winReply) }], '🏅');
      return;
    }
    apply({ stars: 3, minutes: 30 });
    logJournal('🌱', 'contestSetback');
    const mindset = () =>
      line('teacher', t.mindsetQ, [
        { text: t.mindsetGood, run: () => (apply({ mood: 6 }), line('teacher', t.mindsetReply, undefined, '🌱')) },
        { text: t.mindsetBad, run: () => line('teacher', t.mindsetBadReply, [{ text: t.mindsetGood, run: () => (apply({ mood: 6 }), line('teacher', t.mindsetReply, undefined, '🌱')) }]) },
      ]);
    line('narrator', t.setback, [{ text: m().common.ok, run: mindset }], '🐝');
  };
  line('teacher', t.intro, [{ text: m().common.ok, run: () => round(0) }], '🐝');
}

// ---------------------------------------------------------------------------
// Pop quiz (worksheet from the life package).
// ---------------------------------------------------------------------------
function quiz(): void {
  const t = m().classroom.events.quiz;
  line('teacher', t.start, [
    {
      text: t.go,
      run: () =>
        openWorksheet('quiz', (grade) => {
          const stars = grade === 'A' ? 8 : grade === 'B' ? 5 : 2;
          if (grade !== 'C') celebrate();
          apply({ stars, study: 6 });
          logJournal('📝', 'quiz', grade);
          line('teacher', t.result(grade));
        }),
    },
  ], '📝');
}

// ---------------------------------------------------------------------------
// Parents' meeting (家長會): the teacher reports to Mom from your study and growth record.
// ---------------------------------------------------------------------------
const GOOD_KEYS = ['helpedLate', 'helpedHomework', 'helpedStanding', 'ballMadeUp', 'returnedItem', 'calmExplained', 'bullyStood', 'bullyTold', 'forgotHonest', 'contestWin', 'homeworkMadeUp', 'madeUp'];
const UNKIND_KEYS = ['teasedLate', 'mockedStanding', 'ballGrabbed', 'shouted'];

/** Summary of the last four weeks of the growth record, for the meeting. */
export function meetingReport(s: GameState = S()): { good: { icon: string; key: string; arg?: string }[]; unkind: number; studyOk: boolean } {
  const since = dayNumber() - 12;
  const recent = s.journal.filter((j) => j.day > since);
  return {
    good: recent.filter((j) => GOOD_KEYS.includes(j.key)),
    unkind: recent.filter((j) => UNKIND_KEYS.includes(j.key)).length,
    studyOk: s.stats.study >= 45,
  };
}

function parentsMeeting(): void {
  const t = m().classroom.events.parentsMeeting;
  const keys = m().classroom.journalKeys as Record<string, (a?: string) => string>;
  const r = meetingReport();
  markDone();
  let love = 0;
  const how = r.good.length >= 2 && r.studyOk && !r.unkind ? 'great' : r.good.length || r.studyOk ? 'mixed' : 'plan';
  const close = () => {
    love += how === 'great' ? 6 : 2;
    if (how === 'great') note('mom', 'meeting');
    logJournal('👨‍👩‍👧', 'meeting', how);
    burst('heart', 8);
    apply({ love: { mom: love }, mood: 6 });
    line('mom', t.done, undefined, '🤗');
  };
  const unkind = () => {
    if (!r.unkind) return close();
    line('mom', t.unkind, [{ text: t.reflect, run: () => ((love += 3), line('mom', t.reflectReply, [{ text: m().common.ok, run: close }])) }], '🤔');
  };
  const study = () => {
    if (r.studyOk) return line('mom', t.study, [{ text: t.thanksMom, run: () => ((love += 4), unkind()) }]);
    line('mom', t.studyLow, [{ text: t.plan, run: () => ((love += 4), line('mom', t.planReply, [{ text: m().common.ok, run: unkind }])) }]);
  };
  const praise = () => {
    if (!r.good.length) return line('mom', t.nothing, [{ text: m().common.ok, run: study }]);
    love += Math.min(12, r.good.length * 4);
    const lines = r.good.slice(-3).map((j) => `${j.icon} ${keys[j.key]?.(j.arg) ?? j.key}`);
    line('mom', `${t.great}\n${lines.join('\n')}\n${t.proud}`, [{ text: t.thanksMom, run: study }], '🥰');
  };
  line('mom', t.momIntro, [{ text: m().common.ok, run: praise }]);
}

// ---------------------------------------------------------------------------
// Delivery: a light poll that starts each event in the right place and time.
// ---------------------------------------------------------------------------
function tick(): void {
  if (!hasGame() || layers.blocking() || dialogOpen()) return;
  const s = S();
  const ev = todayEvent();
  if (!ev || done()) return;
  const p = s.player;
  switch (ev) {
    case 'forgotHomework':
      if (inClassroom(s) && s.time >= H(8) && s.time < H(12)) (markDone(), forgotHomework());
      return;
    case 'misunderstanding':
      if (inClassroom(s) && s.time >= H(13, 30) && s.time < H(16, 30)) (markDone(), misunderstanding());
      return;
    case 'quiz':
      if (inClassroom(s) && ((s.time >= H(9, 30) && s.time < H(12)) || (s.time >= H(13, 30) && s.time < H(16, 30)))) (markDone(), quiz());
      return;
    case 'contest':
      if (inClassroom(s) && contestOpen() && !s.daily['cls.contestAsked']) {
        s.daily['cls.contestAsked'] = true;
        line('teacher', m().classroom.events.contest.notice, [{ text: m().classroom.events.contest.join, run: spellingBee }, { text: m().classroom.ui.later }], '🐝');
      }
      return;
    case 'parentsMeeting':
      if (p.map === 'home' && s.time >= H(18)) return parentsMeeting();
      if (s.time >= H(19, 30) && !s.daily['cls.momRang']) {
        s.daily['cls.momRang'] = true;
        ringNow('mom');
      }
      return;
    case 'bullying':
      if (p.map === 'town' && bullyLive() && p.y > px(28) && !s.daily['cls.bullyHint']) {
        s.daily['cls.bullyHint'] = true;
        toast(m().classroom.events.bullying.start);
      }
      return;
    case 'lostPencil':
      return;
  }
}

function announce(): void {
  const ev = todayEvent();
  const t = m().classroom.events;
  const msg = ev === 'lostPencil' ? t.lostPencil.start : ev === 'contest' ? t.contest.notice : ev === 'parentsMeeting' ? t.parentsMeeting.notice : null;
  if (msg) window.setTimeout(() => toast(msg), 2600);
}

export function initSchoolEvents(): void {
  // Remember the last day the homework chore was done (chores reset every morning).
  bus.on('change', () => {
    if (hasGame() && S().chores.homework) S().npcTalk['cls.hwDay'] = dayNumber();
  });
  bus.on('dayStart', announce);
  // Bo's talk is taken over only while he's being teased.
  registerTalk((id) => {
    if (id !== 'bo' || !bullyLive() || S().player.map !== 'town') return false;
    bullying();
    return true;
  });
  window.setInterval(tick, 1000);
}

/** Debug helper: force today's event and reset it. */
export function debugEvent(id: EventId): void {
  const s = S();
  todayEvent();
  s.npcTalk['cls.ev'] = EVENTS.indexOf(id) + 1;
  s.daily['cls.ev.done'] = false;
  if (id === 'bullying') return bullying();
  if (id === 'contest') return spellingBee();
  if (id === 'parentsMeeting') return parentsMeeting();
  if (id === 'forgotHomework') return forgotHomework();
  if (id === 'misunderstanding') return misunderstanding();
  if (id === 'quiz') return quiz();
}
